import { Component } from '@theme/component';

/** Enhances native disclosures and the tabbed navigation inside Shopify's dialog. */
class DeluluNavigation extends Component {
  #controller;
  #hoverTimer;
  #activeTab = '1';
  // This Horizon version morphs retained header markup without notifying it.
  // Rebind only this component when its markup or readiness marker is replaced.
  #markupObserver = new MutationObserver((mutations) => {
    if (!this.dataset.ready || mutations.some((mutation) => mutation.type === 'childList')) {
      this.#initialize();
    }
  });

  connectedCallback() {
    super.connectedCallback();
    this.#markupObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-ready'],
    });
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  #initialize() {
    clearTimeout(this.#hoverTimer);
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    if (this.dataset.mode === 'mobile-browse') {
      this.dataset.ready = 'true';
      return;
    }
    if (this.dataset.mode === 'mobile') {
      this.#selectTab(this.querySelector(`[data-tab="${this.#activeTab}"]`) || this.querySelector('[role="tab"]'));
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab) this.#selectTab(tab);
      }, { signal });
      this.addEventListener('keydown', (event) => {
        if (!event.target.matches('[role="tab"]')) return;
        const tabs = [...this.querySelectorAll('[role="tab"]')];
        const index = tabs.indexOf(event.target);
        let next;
        if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
        if (event.key === 'ArrowLeft') next = tabs[(index + tabs.length - 1) % tabs.length];
        if (event.key === 'Home') next = tabs[0];
        if (event.key === 'End') next = tabs.at(-1);
        if (!next) return;
        event.preventDefault();
        this.#selectTab(next);
        next.focus();
      }, { signal });
    } else {
      for (const details of this.querySelectorAll('details')) {
        details.addEventListener('toggle', () => {
          if (details.open) this.#closeOthers(details);
        }, { signal });
        details.addEventListener('pointerenter', (event) => {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(this.#hoverTimer);
          this.#hoverTimer = setTimeout(() => { this.#closeOthers(details); details.open = true; }, 120);
        }, { signal });
        details.addEventListener('pointerleave', () => clearTimeout(this.#hoverTimer), { signal });
      }
      for (const link of this.querySelectorAll('.delulu-nav__direct')) {
        link.addEventListener('pointerenter', () => { clearTimeout(this.#hoverTimer); this.#closeOthers(); }, { signal });
        link.addEventListener('focus', () => this.#closeOthers(), { signal });
      }
      this.addEventListener('pointerleave', () => {
        clearTimeout(this.#hoverTimer);
        this.#hoverTimer = setTimeout(() => {
          if (!this.contains(document.activeElement)) this.#closeOthers();
        }, 180);
      }, { signal });
      this.addEventListener('pointerenter', () => clearTimeout(this.#hoverTimer), { signal });
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = this.querySelector('details[open]');
        this.#closeOthers();
        open?.querySelector('summary')?.focus();
      }, { signal });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.#closeOthers();
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.#closeOthers();
      }, { signal });
      window.addEventListener('resize', () => this.#closeOthers(), { signal });
    }
    this.dataset.ready = 'true';
  }

  #selectTab(tab) {
    if (!tab) return;
    const changed = this.#activeTab !== tab.dataset.tab;
    this.#activeTab = tab.dataset.tab;
    for (const candidate of this.querySelectorAll('[role="tab"]')) {
      candidate.setAttribute('aria-selected', String(candidate === tab));
      candidate.tabIndex = candidate === tab ? 0 : -1;
    }
    for (const panel of this.querySelectorAll('[data-panel]')) {
      panel.hidden = panel.id !== tab.getAttribute('aria-controls');
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', panel.id.replace('-panel-', '-tab-'));
    }
    if (changed) this.querySelector('[data-menu-scroll]')?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #closeOthers(except) {
    for (const details of this.querySelectorAll('details[open]')) {
      if (details !== except) details.open = false;
    }
  }

  disconnectedCallback() {
    this.#markupObserver.disconnect();
    clearTimeout(this.#hoverTimer);
    this.#controller?.abort();
    super.disconnectedCallback();
  }
}

if (!customElements.get('delulu-navigation')) customElements.define('delulu-navigation', DeluluNavigation);
